import { ProcessStatus } from "@src/types";
import _ from "lodash-es";
import { ProcessData, MapCenter, MapMarkersInfo } from "./types";

interface CurveLine {
  path: google.maps.LatLng[];
  geodesic: boolean;
  strokeOpacity: number;
  icons: {
    icon: {
      path: string;
      strokeOpacity: number;
      scale: number;
    };
    offset: string;
    repeat: string;
  }[];
  strokeColor: string;
}

interface CurveArrow {
  position: google.maps.LatLng;
  icon: {
    path: google.maps.SymbolPath;
    rotation: number;
    strokeColor: string;
    scale: number;
  };
}

interface CurveResult {
  line: CurveLine;
  arrow: CurveArrow;
}

interface Point {
  x: number;
  y: number;
}

export const drawDashedCurve = (
  P1: google.maps.LatLng,
  P2: google.maps.LatLng,
  type: number = 0
): CurveResult => {
  const lineLength = window.google.maps.geometry.spherical.computeDistanceBetween(P1, P2);
  const lineHeading = window.google.maps.geometry.spherical.computeHeading(P1, P2);
  let lineHeading1 = lineHeading + 45;
  let lineHeading2 = lineHeading + 135;

  if (type !== 0) {
    lineHeading1 = lineHeading + -45;
    lineHeading2 = lineHeading + -135;
  }

  const pA = window.google.maps.geometry.spherical.computeOffset(P1, lineLength / 2.2, lineHeading1);
  const pB = window.google.maps.geometry.spherical.computeOffset(P2, lineLength / 2.2, lineHeading2);

  const curvedLine = gmapsCubicBezier(P1, pA, pB, P2, 0.0005);
  return curvedLine;
};

class CubicBezier {
  B1(t: number): number {
    return t * t * t;
  }

  B2(t: number): number {
    return 3 * t * t * (1 - t);
  }

  B3(t: number): number {
    return 3 * t * (1 - t) * (1 - t);
  }

  B4(t: number): number {
    return (1 - t) * (1 - t) * (1 - t);
  }

  getBezier(C1: Point, C2: Point, C3: Point, C4: Point, percent: number): Point {
    const pos: Point = {
      x: 0,
      y: 0
    };

    pos.x = C1.x * this.B1(percent) + C2.x * this.B2(percent) + C3.x * this.B3(percent) + C4.x * this.B4(percent);
    pos.y = C1.y * this.B1(percent) + C2.y * this.B2(percent) + C3.y * this.B3(percent) + C4.y * this.B4(percent);

    return pos;
  }
}

const gmapsCubicBezier = (
  latlong1: google.maps.LatLng,
  latlong2: google.maps.LatLng,
  latlong3: google.maps.LatLng,
  latlong4: google.maps.LatLng,
  resolution: number
): CurveResult => {
  const lat1: number = latlong1.lat();
  const long1: number = latlong1.lng();
  const lat2: number = latlong2.lat();
  const long2: number = latlong2.lng();
  const lat3: number = latlong3.lat();
  const long3: number = latlong3.lng();
  const lat4: number = latlong4.lat();
  const long4: number = latlong4.lng();

  const points: Point[] = [];

  for (let it = 0; it <= 1; it += resolution) {
    const cb = new CubicBezier();
    points.push(cb.getBezier(
      { x: lat1, y: long1 },
      { x: lat2, y: long2 },
      { x: lat3, y: long3 },
      { x: lat4, y: long4 },
      it
    ));
  }

  const path: google.maps.LatLng[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    path.push(new window.google.maps.LatLng(points[i].x, points[i].y));
    path.push(new window.google.maps.LatLng(points[i + 1].x, points[i + 1].y));
  }

  const lineSymbol = {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    scale: 3
  };

  const middlePoint = points[Math.floor((points.length - 1) / 2)];
  const nextPoint = points[Math.floor((points.length - 1) / 2) - 1];
  const middleLatLng = new window.google.maps.LatLng(middlePoint.x, middlePoint.y);
  const nextLatLng = new window.google.maps.LatLng(nextPoint.x, nextPoint.y);

  const rot = window.google.maps.geometry.spherical.computeHeading(middleLatLng, nextLatLng);

  return {
    line: {
      path: path,
      geodesic: true,
      strokeOpacity: 0.0,
      icons: [{
        icon: lineSymbol,
        offset: '0',
        repeat: '14px'
      }],
      strokeColor: '#015b9e'
    },
    arrow: {
      position: middleLatLng,
      icon: {
        path: window.google.maps.SymbolPath.FORWARD_OPEN_ARROW,
        rotation: rot,
        strokeColor: '#015b9e',
        scale: 3
      }
    }
  };
};

export const isClickable = (status: number, custom_date: string): boolean => {
  return Boolean((status === Number(ProcessStatus.UPDATED) || (ProcessStatus.MISSED && custom_date)));
};

export const sortProcessByPosition = (a: ProcessData, b: ProcessData): number => {
  try {
    const levelsA = String(a.process_position_b2c).split('.').map(Number);
    const levelsB = String(b.process_position_b2c).split('.').map(Number);
    for (let i = 0; i < Math.max(levelsA.length, levelsB.length); i++) {
      if (levelsA[i] !== levelsB[i]) {
        return levelsA[i] - levelsB[i];
      }
    }
    return 0;
  } catch (e) {
    return 0;
  }
};

export const sortObjectProperties = <T extends object>(obj: T): T => {
  const sortedKeys = Object.keys(obj).sort();
  const sortedObj = {} as T;

  for (let i = 0; i < sortedKeys.length; i++) {
    sortedObj[sortedKeys[i] as keyof T] = obj[sortedKeys[i] as keyof T];
  }

  return sortedObj;
};

interface CurveMapStreets {
  curveMap: CurveResult[];
  streets: [string, number][];
}

export const calculateCurve = (dataMap: ProcessData[] = []): CurveMapStreets => {
  try {
    const curveMap: CurveResult[] = [];
    const streets: [string, number][] = [];

    if (dataMap.length > 1) {
      for (let j = 0; j < dataMap.length - 1; j++) {
        const lat1 = Number(dataMap[j].process_lat);
        const lng1 = Number(dataMap[j].process_long);

        const lat2 = Number(dataMap[j + 1].process_lat);
        const lng2 = Number(dataMap[j + 1].process_long);

        if (!(lat1 === lat2 && lng1 === lng2)) {
          const isDraw = streets.findIndex((street) =>
            street[0].indexOf(`${lat1}-${lng1}`) !== -1 &&
            street[0].indexOf(`${lat2}-${lng2}`) !== -1
          );

          if (isDraw === -1) {
            const curve = drawDashedCurve(
              new window.google.maps.LatLng(lat1, lng1),
              new window.google.maps.LatLng(lat2, lng2)
            );
            curveMap.push(curve);
            streets.push([`${lat1}-${lng1}_${lat2}-${lng2}`, 0]);
          } else {
            if (streets[isDraw][1] === 0) {
              if (streets[isDraw][0].indexOf(`${lat1}-${lng1}`) === 0) {
                const curve = drawDashedCurve(
                  new window.google.maps.LatLng(lat1, lng1),
                  new window.google.maps.LatLng(lat2, lng2),
                  1
                );
                curveMap.push(curve);
              } else {
                const curve = drawDashedCurve(
                  new window.google.maps.LatLng(lat1, lng1),
                  new window.google.maps.LatLng(lat2, lng2)
                );
                curveMap.push(curve);
              }
              streets[isDraw][1] = streets[isDraw][1] + 1;
            } else {
              const middleLatLng = new window.google.maps.LatLng((lat1 + lat2) / 2, (lng1 + lng2) / 2);
              curveMap.push({
                line: {
                  path: [
                    new window.google.maps.LatLng(lat1, lng1),
                    new window.google.maps.LatLng(lat2, lng2)
                  ],
                  geodesic: true,
                  strokeOpacity: 0.0,
                  icons: [{
                    icon: {
                      path: 'M 0,-1 0,1',
                      strokeOpacity: 1,
                      scale: 3
                    },
                    offset: '0',
                    repeat: '14px'
                  }],
                  strokeColor: '#015b9e'
                },
                arrow: {
                  position: middleLatLng,
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_OPEN_ARROW,
                    strokeColor: '#015b9e',
                    scale: 3,
                    rotation: window.google.maps.geometry.spherical.computeHeading(
                      middleLatLng,
                      new window.google.maps.LatLng(lat2, lng2)
                    )
                  }
                }
              });
            }
          }
        }
      }
    }

    return { curveMap, streets };
  } catch (e) {
    console.error(e);
    return { curveMap: [], streets: [] };
  }
};

export const calculateCurveV2 = (markersPair: ProcessData[] = []): CurveMapStreets => {
  try {
    const curveMap: CurveResult[] = [];
    const streets: [string, number][] = [];

    if (markersPair.length > 1) {
      for (let j = 0; j < markersPair.length - 1; j++) {
        const lat1 = parseFloat(String(markersPair[j].latitude || 0));
        const lng1 = parseFloat(String(markersPair[j].longitude || 0));

        const lat2 = parseFloat(String(markersPair[j + 1].latitude || 0));
        const lng2 = parseFloat(String(markersPair[j + 1].longitude || 0));

        if (!(lat1 === lat2 && lng1 === lng2)) {
          const isDraw = streets.findIndex((street) =>
            street[0].indexOf(`${lat1}-${lng1}`) !== -1 &&
            street[0].indexOf(`${lat2}-${lng2}`) !== -1
          );

          if (isDraw === -1) {
            const curve = drawDashedCurve(
              new window.google.maps.LatLng(lat1, lng1),
              new window.google.maps.LatLng(lat2, lng2)
            );
            curveMap.push(curve);
            streets.push([`${lat1}-${lng1}_${lat2}-${lng2}`, 0]);
          } else {
            if (streets[isDraw][1] === 0) {
              if (streets[isDraw][0].indexOf(`${lat1}-${lng1}`) === 0) {
                const curve = drawDashedCurve(
                  new window.google.maps.LatLng(lat1, lng1),
                  new window.google.maps.LatLng(lat2, lng2),
                  1
                );
                curveMap.push(curve);
              } else {
                const curve = drawDashedCurve(
                  new window.google.maps.LatLng(lat1, lng1),
                  new window.google.maps.LatLng(lat2, lng2)
                );
                curveMap.push(curve);
              }
              streets[isDraw][1] = streets[isDraw][1] + 1;
            } else {
              const middleLatLng = new window.google.maps.LatLng((lat1 + lat2) / 2, (lng1 + lng2) / 2);
              curveMap.push({
                line: {
                  path: [
                    new window.google.maps.LatLng(lat1, lng1),
                    new window.google.maps.LatLng(lat2, lng2)
                  ],
                  geodesic: true,
                  strokeOpacity: 0.0,
                  icons: [{
                    icon: {
                      path: 'M 0,-1 0,1',
                      strokeOpacity: 1,
                      scale: 3
                    },
                    offset: '0',
                    repeat: '14px'
                  }],
                  strokeColor: '#015b9e'
                },
                arrow: {
                  position: middleLatLng,
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_OPEN_ARROW,
                    strokeColor: '#015b9e',
                    scale: 3,
                    rotation: window.google.maps.geometry.spherical.computeHeading(
                      middleLatLng,
                      new window.google.maps.LatLng(lat2, lng2)
                    )
                  }
                }
              });
            }
          }
        }
      }
    }

    return { curveMap, streets };
  } catch (e) {
    console.error(e);
    return { curveMap: [], streets: [] };
  }
};

export const getNestedConnectedOrders = (arr: any[]): ProcessData[] => {
  try {
    let groups: ProcessData[] = [];

    for (const item of arr) {

      // if (item.b2c_map_view_visible) {

      if (item.processes) {
        groups = [...groups, ...item.processes];

        for (const process of item.processes) {
          const connected_orders = process.connected_orders || [];
          const tmp = getNestedConnectedOrders(connected_orders);
          if (tmp.length > 0) {
            groups.splice(groups.indexOf(process), 0, ...tmp);
          }
        }
      }

      // }
    }

    return groups;
  } catch (e) {
    console.error("e", (e as Error).message);
    return [];
  }
};

export const groupByProcessPosition = (processes: ProcessData[]): ProcessData[][] => {
  const result: ProcessData[][] = [];
  const groups: Record<string, ProcessData[]> = {};

  processes.forEach((obj) => {
    const process_position_b2c = String(obj.process_position_b2c);
    const prefix = process_position_b2c.split('.').slice(0, -1).join('.');

    if (!groups[prefix]) {
      groups[prefix] = [];
      result.push(groups[prefix]);
    }

    groups[prefix].push(obj);
  });

  result.forEach((items) => {
    items.forEach(item => {
      const process_position_b2c = String(item.process_position_b2c);
      const prefixParent = process_position_b2c.split('.').slice(0, -2).join('.');

      const exists = items.find((process) =>
        String(process.process_position_b2c) === prefixParent
      );

      const parent = processes.find((process) =>
        String(process.process_position_b2c) === prefixParent
      );

      if (parent && !exists) {
        items.push(parent);
      }
    });
  });

  return result;
};

/**
 * Same as groupByProcessPosition() but this one does not have `process_position_b2c`,
 * only `process_position` available
 */
export const groupByProcessDetailPosition = (processes: any[]): any[][] => {
  const result: any[][] = [];
  const groups: Record<string, any[]> = {};

  processes.forEach((obj) => {
    const process_position_b2c = String(obj.process_position);
    const prefix = process_position_b2c.split('.').slice(0, -1).join('.');

    if (!groups[prefix]) {
      groups[prefix] = [];
      result.push(groups[prefix]);
    }

    groups[prefix].push(obj);
  });

  result.forEach((items) => {
    items.forEach(item => {
      const process_position_b2c = String(item.process_position);
      const prefixParent = process_position_b2c.split('.').slice(0, -2).join('.');

      const exists = items.find((process) =>
        String(process.process_position) === prefixParent
      );

      const parent = processes.find((process) =>
        String(process.process_position) === prefixParent
      );

      if (parent && !exists) {
        items.push(parent);
      }
    });
  });

  return result;
};

export const isValidLatLng = (lat: number, lng: number): boolean => {
  if (isNaN(lat) || isNaN(lng)) return false;

  try {
    const latLng = new window.google.maps.LatLng(lat, lng);
    return latLng.lat() === lat &&
      (lng >= -180 && lng <= 180 ? latLng.lng() === lng : latLng.lng() === (lng + 360) % 360 - 180);
  } catch (e) {
    console.error("Invalid LatLng", lat, lng, e);
    return false;
  }
};

export const getProcessLatLng = ({ lat, lng }: { lat: number, lng: number }): MapCenter => {
  return {
    lat: parseFloat(String(lat)),
    lng: parseFloat(String(lng))
  };
};

interface MarkersDistanceInfo {
  maxDistance: number;
  zoomLevel: number;
  center: MapCenter | null;
}

export const getMarkersDistanceInfo = (markers: MapCenter[]): MarkersDistanceInfo => {
  if (!markers || markers.length === 0) {
    console.warn("No markers provided.");
    return { maxDistance: 0, zoomLevel: 16, center: null };
  }

  function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const toRad = (angle: number) => (angle * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  let maxDistance = 0;
  for (let i = 0; i < markers.length; i++) {
    for (let j = i + 1; j < markers.length; j++) {
      const distance = haversineDistance(
        markers[i].lat, markers[i].lng,
        markers[j].lat, markers[j].lng
      );
      if (distance > maxDistance) {
        maxDistance = distance;
      }
    }
  }

  let zoomLevel: number;
  if (maxDistance > 15000) {
    zoomLevel = 1.55;
  } else if (maxDistance > 10000) {
    zoomLevel = 2;
  } else if (maxDistance > 5000) {
    zoomLevel = 3;
  } else if (maxDistance > 2000) {
    zoomLevel = 3;
  } else if (maxDistance > 1000 && maxDistance < 2000) {
    zoomLevel = 5;
  } else if (maxDistance > 500) {
    zoomLevel = 8;
  } else if (maxDistance > 200) {
    zoomLevel = 9;
  } else if (maxDistance > 100) {
    zoomLevel = 10;
  } else if (maxDistance > 50) {
    zoomLevel = 11;
  } else if (maxDistance > 20) {
    zoomLevel = 12;
  } else if (maxDistance > 10) {
    zoomLevel = 13;
  } else if (maxDistance > 5) {
    zoomLevel = 14;
  } else if (maxDistance > 2) {
    zoomLevel = 15;
  } else {
    zoomLevel = 16;
  }

  let latSum = 0, lngSum = 0;
  markers.forEach(marker => {
    latSum += marker.lat;
    lngSum += marker.lng;
  });

  const center = {
    lat: latSum / markers.length,
    lng: lngSum / markers.length
  };

  console.log("Map Adjust Info:", {
    maxDistance,
    zoomLevel,
    center
  });

  return {
    maxDistance,
    zoomLevel,
    center
  };
};

export const getCenterOfMarkers = (processes: ProcessData[] = []): MapMarkersInfo | null => {
  if (!processes || processes.length === 0) {
    return null;
  }

  const seen = new Set<string>();

  // Extract unique markers from processes
  const markers = processes
    .reduce((acc: MapCenter[], process) => {
      const marker: MapCenter = {
        lat: parseFloat(String(process.process_lat || 0)),
        lng: parseFloat(String(process.process_long || 0))
      };

      acc.push(marker);
      return acc;
    }, [])
    .filter(marker => {
      const key = `${marker.lat},${marker.lng}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

  const result = getMarkersDistanceInfo(markers);

  return {
    ...result,
    markers: processes.map(p => ({
      process_lat: String(p.process_lat || 0),
      process_long: String(p.process_long || 0)
    }))
  };
};

export const isSequenceProcesses = (processes: ProcessData[] = []): boolean => {
  try {
    const values = processes
      .map(obj => Number(obj.custom_order || 0))
      .sort((a, b) => a - b);

    for (let i = 1; i < values.length; i++) {
      if (values[i] !== values[i - 1] + 1) {
        return false;
      }
    }

    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const formatSequentialRanges = (processes: ProcessData[] = []): string => {
  try {
    const values = processes
      .map(obj => Number(obj.custom_order || 0))
      .sort((a, b) => a - b);

    if (values.length === 0) return '';

    const result: string[] = [];
    let start = values[0];
    let end = values[0];

    for (let i = 1; i < values.length; i++) {
      if (values[i] === end + 1) {
        end = values[i]; // Extend the sequence
      } else {
        // Push the range or single number
        result.push(start === end ? `${start}` : `${start}-${end}`);
        start = values[i];
        end = values[i];
      }
    }

    // Push the last range
    result.push(start === end ? `${start}` : `${start}-${end}`);

    return result.join(', ');
  } catch (e) {
    console.error(e);
    return '';
  }
};